import SearchListContent from './SearchListContent';
import ShopCategory from '../components/ShopCategory';
import ShopInfo from '../components/ShopInfo';
import ShopNavBar from '../components/ShopNavBar';
import ShopBanner from '../components/ShopBanner';
import jsurl from 'jsurl';
import { Spin } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import ListFilter from 'comps/ListFilter';
import { FeatureEnum, isFeatureActive } from 'libs/features';
import { useRouter } from 'next/router';
import useAreaCookie from 'hooks/useAreaCookie';
import { Category } from 'comps/CategoryBreadcrumb';
import { clone } from 'rambdax';
import { LocContext } from 'libs/contexts';
import ListRuleBar from 'comps/ListRuleBar';
import { getGoodsResult } from 'api/shop';
import { useNetActionPriceContext } from 'libs/contexts';

type ListData = {
  total: number;
  pageNo: number;
  pageSize: number;
  totalPage: number;
  datas: Array<SpuEntity>;
};
export default function ShopList({ shopData }) {
  const { location } = useContext(LocContext);
  const [filterList, setFilterList] = useState([]);
  const [listData, setListData] = useState<ListData>({
    total: null,
    pageNo: null,
    totalPage: null,
    pageSize: 30,
    datas: [],
  });
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const searchParams = useRef<any>({
    sorts: [{ sortField: '', orderBy: 1 }],
    keywords: '',
  });
  const primaryColor = 'var(--primaryColor)';
  const [shopCustomCategoryList, setShopCustomCategoryList] = useState<Array<Category>>([]);
  const { areaCode, getCookieAreaCode } = useAreaCookie();
  const getNetPriceList = useNetActionPriceContext();
  useEffect(() => {
    if (!areaCode) return;
    (async () => {
      updateParams();
      getList();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, areaCode, location]);

  // 检查并更新searchParams
  const updateParams = () => {
    const { query } = router;
    // 路由有参数，更新searchParams
    if (query.params) {
      searchParams.current = {
        ...jsurl.parse(query.params),
      };
      // 更新searchParams后，若keywords有值，则要解码url编码
      if (searchParams.current.keywords) {
        searchParams.current.keywords = decodeURIComponent(searchParams.current.keywords);
      }
    }
  };

  // 获取商品列表
  async function getList() {
    const { shopSysNo, pageSysNo, isCustomCategory } = router.query;
    const catIdArr = searchParams.current?.catId?.toString().split(',') ?? null;
    const categorySysNoList = catIdArr ? [catIdArr[catIdArr.length - 1]] : [];
    const json = {
      areaLevelCode: getCookieAreaCode(),
      pageNo: 1,
      pageSize: 20,
      shopSysNoList: [shopSysNo],
      ...searchParams.current,
      shopSkuListPageSysNo: pageSysNo,
    };
    if (isCustomCategory) {
      json.shopCustomCategorySysNoList = categorySysNoList;
      json.catId = undefined;
    } else {
      json.shopCustomCategorySysNoList = categorySysNoList;
    }
    setLoading(true);
    const res = await getGoodsResult({ json });
    setLoading(false);
    if (res.code === 200) {
      setListData(res.data.spuList);
      setFilterList(combineFilter(res.data));
      setShopCustomCategoryList(shopData.shopCustomCategoryTreeDTOList);
      getNetPriceList(res.data.spuList?.datas, (list) => {
        setListData({
          ...res.data.spuList,
          datas: list,
        });
      });
    }
  }

  // 结合商品过滤条件
  const combineFilter = (sku) => {
    const arr = [];
    if (!!sku.upstreamSupplierList?.length) {
      arr.push({
        type: 'upstreamSupplierSysNoList',
        name: '配送商',
        values: sku.upstreamSupplierList.map((v) => ({
          ...v,
          name: v.supplierName,
          val: [
            {
              type: 'upstreamSupplierSysNoList',
              sysNo: v.sysNo,
              name: v.supplierName,
            },
          ],
        })),
      });
    }
    if (sku.spuAttributeList?.length > 0) {
      sku.spuAttributeList.forEach((v) => {
        arr.push({
          type: 'spuAttributeList',
          ...v,
          values: v.values?.map((v2) => ({
            ...v2,
            type: 'spuAttributeList',
            val: [
              {
                type: 'spuAttributeList',
                sysNo: v2.sysNo,
                pSysNo: v.sysNo,
                name: v2.name,
              },
            ],
          })),
        });
      });
    }
    if (sku.brandList?.length > 0) {
      arr.push({
        type: 'brands',
        name: '品牌',
        values: sku.brandList.map((v) => ({
          ...v,
          val: [
            {
              type: 'brands',
              sysNo: v.sysNo,
            },
          ],
        })),
      });
    }

    const newArr = [];
    const list = clone(arr);
    const relList = [];
    list.forEach((v) => {
      const index = newArr.findIndex((c) => c.name === v.name);
      if (index > -1) {
        v.values.forEach((m) => {
          const index2 = newArr[index].values.findIndex((c) => c.name === m.name);
          if (index2 > -1) {
            newArr[index].values[index2].val.push(m.val[0]);
            relList.push({ ...m, relative: newArr[index].values[index2] });
          } else {
            newArr[index].values.push(m);
          }
        });
      } else {
        newArr.push(v);
      }
    });

    return newArr;
  };
  function updateSearch(newParams) {
    const { query } = router;
    if (!newParams.pageNo) {
      newParams.pageNo = 1;
    }

    /*
    当前这种方式调用router.push，页面不会更新，状态不会丢，拿到的router.query不是最新的，是页面初始化时的router.query，会造成搜索条件状态始终是初始值
    */
    searchParams.current = { ...searchParams.current, ...newParams };
    const newQuery = {
      ...query,
      params: jsurl.stringify(searchParams.current),
    };
    const url = {
      pathname: router.pathname,
      query: newQuery,
    };

    const queryStr = Object.entries(newQuery)
      .map((item) => item.join('='))
      .join('&');

    const urlStr = url.pathname + '?' + queryStr;

    router.push(url, urlStr, {
      shallow: true, // 不会重新加载页面
    });
  }

  // 过滤条件修改后触发
  function handleListFilterUpdateSearch(newParams) {
    if (!newParams) {
      // eslint-disable-next-line no-param-reassign
      newParams = {};
    }
    newParams.pageNo = 1;
    updateSearch(newParams);
  }
  return (
    <>
      <div className="shop-container">
        <ShopBanner shop={shopData} />
        <ShopNavBar pageType={'index'} />
        <div className="shop-wrap">
          <div className="shop-left">
            <ShopInfo shop={shopData} />
            <ShopCategory twoShopCustomCategoryTreeDTOList={shopCustomCategoryList} />
          </div>
          <div className="shop-right">
            <div className="search-info">
              {searchParams.current.keywords && (
                <span className="search-key">搜索{` "${searchParams.current.keywords}"`}</span>
              )}
              <span className="result-info">
                共
                <span className="total-number">
                  {listData.total > 10000 ? listData.total / 10000 + '万+' : listData.total}
                </span>
                件相关产品
              </span>
            </div>
            <ListFilter
              primaryColor={primaryColor}
              filterList={filterList}
              updateSearch={
                isFeatureActive(FeatureEnum.FixShopSkuList)
                  ? handleListFilterUpdateSearch
                  : updateSearch
              }
            />
            <ListRuleBar
              primaryColor={primaryColor}
              listData={listData}
              updateSearch={updateSearch}
            />
            {loading ? (
              <div className="loading-wrap">
                <Spin />
              </div>
            ) : (
              <SearchListContent listData={listData} updateSearch={updateSearch} />
            )}
          </div>
        </div>
      </div>

      <style jsx>
        {`
          .shop-container {
            margin-bottom: 20px;
          }
          .shop-right{
            float: right;
            width: 956px;
            height: auto;
            margin-top: 20px;
          }
          .shop-left{
            float: left;
            width: 224px;
            height: auto;
            margin-top: 20px;
          }
          .shop-wrap{
            position: relative;
            width: 1200px;
            margin: 0 auto;
          }
          .shop-wrap:after{
            clear: both;
            display: block;
            content:"";
          }
          .search-info {
            font-size: 12px;
            display: inline-block;
            margin-bottom: 20px;
            width: 100%;
          }
          .search-key{
            color: #999;
          }
          .result-info{
            float: right;
            color: #666;
          }
          .total-number {
            color: #018DD7;
            margin: 0 5px;
          }
          `}
      </style>
    </>
  );
}
